import {
  ref as rtdbRef,
  set as rtdbSet,
  get as rtdbGet,
  onValue as rtdbOnValue,
  remove as rtdbRemove,
  goOnline as rtdbGoOnline
} from 'firebase/database';
import { auth, rtdb, writeAuditLog, saveGameRecord } from '../firebase';
import { GameHand, GameRecord } from '../models/FirestoreModels';

const RTDB_BASE_URL = 'https://atlcardsharks-a9914-default-rtdb.firebaseio.com';
const FAST_POLL_INTERVAL_MS = 1500;

async function getAuthTokenFast(timeoutMs: number = 800): Promise<string | null> {
  try {
    const currentUser = (auth as any)?.currentUser;
    if (!currentUser || typeof currentUser.getIdToken !== 'function') return null;
    return await Promise.race([
      currentUser.getIdToken() as Promise<string>,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs))
    ]);
  } catch {
    return null;
  }
}

async function putRtdbRestWithAuth(path: string, payload: any, timeoutMs: number = 1500): Promise<boolean> {
  try {
    if (typeof fetch !== 'function') return false;
    const token = await getAuthTokenFast();
    if (!token) return false;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const res = await fetch(`${RTDB_BASE_URL}/${path}.json?auth=${encodeURIComponent(token)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller?.signal
      });
      return res.ok;
    } finally {
      if (timer) clearTimeout(timer);
    }
  } catch {
    return false;
  }
}

async function getRtdbRestWithAuth<T = any>(path: string, timeoutMs: number = 1500): Promise<{ ok: boolean; data: T | null }> {
  try {
    if (typeof fetch !== 'function') return { ok: false, data: null };
    const token = await getAuthTokenFast();
    if (!token) return { ok: false, data: null };

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const res = await fetch(`${RTDB_BASE_URL}/${path}.json?auth=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller?.signal
      });
      if (!res.ok) return { ok: false, data: null };
      const data = (await res.json()) as T | null;
      return { ok: true, data };
    } finally {
      if (timer) clearTimeout(timer);
    }
  } catch {
    return { ok: false, data: null };
  }
}

async function deleteRtdbRestWithAuth(path: string, timeoutMs: number = 1500): Promise<boolean> {
  try {
    if (typeof fetch !== 'function') return false;
    const token = await getAuthTokenFast();
    if (!token) return false;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const res = await fetch(`${RTDB_BASE_URL}/${path}.json?auth=${encodeURIComponent(token)}`, {
        method: 'DELETE',
        signal: controller?.signal
      });
      return res.ok;
    } finally {
      if (timer) clearTimeout(timer);
    }
  } catch {
    return false;
  }
}

export interface PendingScorecardSummary {
  scorecardId: string;
  creatorId: string;
  creatorName: string;
  creatorPhotoUrl?: string | null;
  partnerId?: string | null;
  partnerName?: string | null;
  partnerPhotoUrl?: string | null;
  opponent1Id?: string | null;
  opponent1Name?: string | null;
  opponent1PhotoUrl?: string | null;
  opponent2Id?: string | null;
  opponent2Name?: string | null;
  opponent2PhotoUrl?: string | null;
  team1Score: number;
  team2Score: number;
  handsCount: number;
  latestHandSummary?: string | null;
  unconfirmedHandIndex?: number | null;
  unconfirmedHandNumber?: number | null;
  isUserCreator?: boolean;
  isUserOpponent: boolean;
  isUserPartner: boolean;
  isSuperUser?: boolean;
  isMatchConfirmed?: boolean;
  confirmedByMemberId?: string | null;
  confirmedByMemberName?: string | null;
  confirmedAt?: string | null;
  hands?: GameHand[];
  matchStage?: 'hand1' | 'hand2' | 'hand3' | 'summary' | null;
  isPendingVerification?: boolean;
  updatedAt: number;
}

export interface LiveScorecardData {
  scorecardId: string;
  creatorId: string;
  creatorName: string;
  creatorPhotoUrl?: string | null;
  partnerId?: string | null;
  partnerName?: string | null;
  partnerPhotoUrl?: string | null;
  opponent1Id?: string | null;
  opponent1Name?: string | null;
  opponent1PhotoUrl?: string | null;
  opponent2Id?: string | null;
  opponent2Name?: string | null;
  opponent2PhotoUrl?: string | null;
  team1Score: number;
  team2Score: number;
  hands: GameHand[];
  matchStage?: 'hand1' | 'hand2' | 'hand3' | 'summary' | null;
  playedDate: string;
  locationName?: string | null;
  isMatchConfirmed?: boolean;
  confirmedByMemberId?: string | null;
  confirmedByMemberName?: string | null;
  confirmedAt?: string | null;
  updatedAt: number;
}

/**
 * Recursively strips undefined values and normalizes properties for Firebase Realtime Database.
 * RTDB throws an unhandled serialization error if any property in an object passed to set() or update() is undefined.
 */
export function sanitizeForRtdb<T>(val: T): T {
  if (val === undefined) {
    return null as any;
  }
  if (val === null || typeof val !== 'object') {
    return val;
  }
  if (Array.isArray(val)) {
    return val.map((item) => sanitizeForRtdb(item)) as any;
  }
  const result: Record<string, any> = {};
  for (const [k, v] of Object.entries(val)) {
    if (v !== undefined) {
      result[k] = sanitizeForRtdb(v);
    }
  }
  return result as any;
}

export async function publishLiveScorecard(scorecard: LiveScorecardData): Promise<void> {
  if (!scorecard.scorecardId) return;

  try {
    try {
      rtdbGoOnline(rtdb);
    } catch {}

    const nowTs = Date.now();
    const rootPath = `live_scorecards/${scorecard.scorecardId}`;
    const cleanScorecard = sanitizeForRtdb({
      scorecardId: scorecard.scorecardId,
      creatorId: scorecard.creatorId || '',
      creatorName: scorecard.creatorName || 'Member',
      creatorPhotoUrl: scorecard.creatorPhotoUrl || null,
      partnerId: scorecard.partnerId || null,
      partnerName: scorecard.partnerName || null,
      partnerPhotoUrl: scorecard.partnerPhotoUrl || null,
      opponent1Id: scorecard.opponent1Id || null,
      opponent1Name: scorecard.opponent1Name || null,
      opponent1PhotoUrl: scorecard.opponent1PhotoUrl || null,
      opponent2Id: scorecard.opponent2Id || null,
      opponent2Name: scorecard.opponent2Name || null,
      opponent2PhotoUrl: scorecard.opponent2PhotoUrl || null,
      team1Score: scorecard.team1Score || 0,
      team2Score: scorecard.team2Score || 0,
      hands: scorecard.hands || [],
      matchStage: scorecard.matchStage || 'hand1',
      playedDate: scorecard.playedDate || new Date().toISOString().slice(0, 10),
      locationName: scorecard.locationName || 'Clubhouse',
      isMatchConfirmed: Boolean(scorecard.isMatchConfirmed),
      confirmedByMemberId: scorecard.confirmedByMemberId || null,
      confirmedByMemberName: scorecard.confirmedByMemberName || null,
      confirmedAt: scorecard.confirmedAt || null,
      updatedAt: nowTs
    });

    // Identify unconfirmed hands
    let unconfirmedHandIndex: number | null = null;
    let unconfirmedHandNumber: number | null = null;
    let latestHandSummary = 'Match in progress';

    if (scorecard.hands && scorecard.hands.length > 0) {
      const lastHand = scorecard.hands[scorecard.hands.length - 1];
      const bidSuit = lastHand.trumpType === 'no_trump' ? 'NT' : (lastHand.trumpSuit?.toUpperCase() || 'TRUMP');
      latestHandSummary = `Hand ${lastHand.handNumber}: ${lastHand.bid} ${bidSuit} ${lastHand.outcome === 'made' ? 'Made' : 'Set'}`;

      const unconfirmed = scorecard.hands.find((h) => !h.isConfirmed);
      if (unconfirmed) {
        unconfirmedHandIndex = unconfirmed.handNumber - 1;
        unconfirmedHandNumber = unconfirmed.handNumber;
      }
    }

    const involved = [
      { id: scorecard.creatorId, isPartner: false, isOpponent: false },
      { id: scorecard.partnerId, isPartner: true, isOpponent: false },
      { id: scorecard.opponent1Id, isPartner: false, isOpponent: true },
      { id: scorecard.opponent2Id, isPartner: false, isOpponent: true }
    ].filter((p): p is { id: string; isPartner: boolean; isOpponent: boolean } => Boolean(p.id));

    // Fire WebSocket SDK writes in background (updates local in-memory listeners at 0ms)
    const sdkWritePromise = Promise.all([
      rtdbSet(rtdbRef(rtdb, rootPath), cleanScorecard),
      ...involved.map((player) => {
        const summary: PendingScorecardSummary = sanitizeForRtdb({
          scorecardId: scorecard.scorecardId,
          creatorId: scorecard.creatorId || '',
          creatorName: scorecard.creatorName || 'Member',
          creatorPhotoUrl: scorecard.creatorPhotoUrl || null,
          partnerId: scorecard.partnerId || null,
          partnerName: scorecard.partnerName || null,
          partnerPhotoUrl: scorecard.partnerPhotoUrl || null,
          opponent1Id: scorecard.opponent1Id || null,
          opponent1Name: scorecard.opponent1Name || null,
          opponent1PhotoUrl: scorecard.opponent1PhotoUrl || null,
          opponent2Id: scorecard.opponent2Id || null,
          opponent2Name: scorecard.opponent2Name || null,
          opponent2PhotoUrl: scorecard.opponent2PhotoUrl || null,
          team1Score: scorecard.team1Score || 0,
          team2Score: scorecard.team2Score || 0,
          handsCount: scorecard.hands?.length || 0,
          latestHandSummary,
          unconfirmedHandIndex,
          unconfirmedHandNumber,
          isUserPartner: player.isPartner,
          isUserOpponent: player.isOpponent,
          isMatchConfirmed: Boolean(scorecard.isMatchConfirmed),
          confirmedByMemberId: scorecard.confirmedByMemberId || null,
          confirmedByMemberName: scorecard.confirmedByMemberName || null,
          confirmedAt: scorecard.confirmedAt || null,
          hands: scorecard.hands || [],
          updatedAt: nowTs
        });
        return rtdbSet(
          rtdbRef(rtdb, `user_live_scorecards/${player.id}/${scorecard.scorecardId}`),
          summary
        );
      })
    ]);

    // Simultaneously fire direct HTTPS REST PUT (~50ms) so cold mobile WebSockets never delay delivery
    const restSuccess = await putRtdbRestWithAuth(rootPath, cleanScorecard);
    if (!restSuccess) {
      await sdkWritePromise;
    } else {
      sdkWritePromise.catch(() => {});
    }

    console.log(
      `[liveScorecardSyncService] Published live scorecard ${scorecard.scorecardId} (REST=${restSuccess}) for ${involved.length} seated players`
    );
  } catch (err: any) {
    console.error('[liveScorecardSyncService] CRITICAL: Failed to publish live scorecard to RTDB:', err);
    try {
      await writeAuditLog(
        'Scorecard RTDB Sync Error',
        scorecard.scorecardId,
        `Failed to publish live scorecard ${scorecard.scorecardId}: ${err?.message || String(err)}`
      );
    } catch (auditErr) {
      console.warn('[liveScorecardSyncService] Could not write audit log for sync failure:', auditErr);
    }
  }
}

/**
 * Subscribes to real-time updates for a specific scorecard.
 * Used by the recorder and reviewer to watch for opponent confirmations in real time.
 */
export function subscribeToLiveScorecard(
  scorecardId: string,
  callback: (data: LiveScorecardData | null) => void
): () => void {
  if (!scorecardId) return () => {};

  try {
    rtdbGoOnline(rtdb);
  } catch {}

  let lastSig = '__INIT__';
  const emitIfChanged = (val: LiveScorecardData | null) => {
    const nextSig = JSON.stringify(val);
    if (nextSig === lastSig) return;
    lastSig = nextSig;
    callback(val);
  };

  const pullRest = () => {
    getRtdbRestWithAuth<LiveScorecardData>(`live_scorecards/${scorecardId}`)
      .then((res) => {
        if (res.ok) {
          emitIfChanged(res.data || null);
        }
      })
      .catch(() => {});
  };

  pullRest();

  const unsubscribe = rtdbOnValue(scorecardsRef(scorecardId), (snap) => {
    if (snap.exists()) {
      emitIfChanged(snap.val() as LiveScorecardData);
    } else {
      emitIfChanged(null);
    }
  });

  const intervalId = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      pullRest();
    }
  }, FAST_POLL_INTERVAL_MS);

  return () => {
    clearInterval(intervalId);
    unsubscribe();
  };
}

function scorecardsRef(scorecardId: string) {
  return rtdbRef(rtdb, `live_scorecards/${scorecardId}`);
}

export const GENERIC_PLACEHOLDERS = new Set([
  "?",
  "partner",
  "opponent",
  "opp",
  "opp 1",
  "opp 2",
  "opponent 1",
  "opponent 2",
  "member",
  "player",
  "guest",
  "tbd",
  "unassigned",
  "none",
  "unknown"
]);

/**
 * Robust matcher for determining whether a seated player (by id or name) matches
 * any of the user's candidate IDs or name tokens (e.g., 'Hub' matching 'Hub Harrison').
 */
export function isScorecardPlayerMatch(
  player: { id?: string | null; name?: string | null } | null | undefined,
  candidateIds: string[],
  candidateNames: string[]
): boolean {
  if (!player) return false;

  // 1. Direct ID match
  if (player.id) {
    const pId = player.id.trim().toLowerCase();
    if (pId && candidateIds.some((id) => id && id.trim().toLowerCase() === pId)) {
      return true;
    }
  }

  // 2. Tokenized & substring Name match
  if (player.name) {
    const target = player.name.trim().toLowerCase();
    if (!target || target.length < 2 || GENERIC_PLACEHOLDERS.has(target)) return false;
    const targetTokens = target.split(/\s+/).filter(Boolean);

    for (const rawName of candidateNames) {
      if (!rawName) continue;
      const cand = rawName.trim().toLowerCase();
      if (!cand || cand.length < 2 || GENERIC_PLACEHOLDERS.has(cand)) continue;

      // Exact match
      if (cand === target) return true;

      const candTokens = cand.split(/\s+/).filter(Boolean);

      // Multi-word vs Multi-word:
      // If both target and candidate have 2+ words (e.g. "Frank Bethel" vs "Fantastic Frank"),
      // they MUST match on all words to be considered the same player.
      // Sharing a single common first name (e.g. "Frank") must NEVER match different people.
      if (candTokens.length >= 2 && targetTokens.length >= 2) {
        const candSubsetOfTarget = candTokens.every((tok) => targetTokens.includes(tok));
        const targetSubsetOfCand = targetTokens.every((tok) => candTokens.includes(tok));
        if (candSubsetOfTarget || targetSubsetOfCand) {
          return true;
        }
        continue;
      }

      // Single-word nickname vs Multi-word name:
      // (e.g. candidate has nickname "Hub" and seated name is "Hub Harrison",
      // or candidate is "Hub Harrison" and seated as "Hub")
      if (candTokens.length === 1 && targetTokens.length >= 2) {
        const singleTok = candTokens[0];
        if (singleTok.length >= 3 && targetTokens.includes(singleTok)) {
          return true;
        }
      } else if (targetTokens.length === 1 && candTokens.length >= 2) {
        const singleTok = targetTokens[0];
        if (singleTok.length >= 3 && candTokens.includes(singleTok)) {
          return true;
        }
      } else if (candTokens.length === 1 && targetTokens.length === 1) {
        if (candTokens[0] === targetTokens[0]) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Checks if a given player is busy according to the busy members map.
 */
export function isPlayerBusy(
  player: { id?: string | null; name?: string | null } | null | undefined,
  busyMembers: Record<string, { scorecardId: string; creatorName?: string }> | null | undefined
): { scorecardId: string; creatorName?: string } | null {
  if (!player || !busyMembers) return null;

  if (player.id) {
    const pId = player.id.trim().toLowerCase();
    if (busyMembers[player.id]) return busyMembers[player.id];
    if (busyMembers[pId]) return busyMembers[pId];
  }

  if (player.name) {
    const clean = player.name.trim().toLowerCase();
    if (!clean || GENERIC_PLACEHOLDERS.has(clean)) return null;

    // 1. Direct full name match
    if (busyMembers[clean]) return busyMembers[clean];

    const tokens = clean.split(/\s+/).filter(Boolean);

    // 2. If queried with a single token (e.g. "Hub" or "Harrison"), check single token index
    if (tokens.length === 1 && tokens[0].length >= 2) {
      if (busyMembers[tokens[0]]) {
        return busyMembers[tokens[0]];
      }
    }
  }

  return null;
}

/**
 * Evaluates whether a scorecard is in a "pending verification" state.
 * A match is pending verification when it is not yet confirmed by the opponent/admin,
 * and has reached the match summary stage, target score (>= 7 in Bid Whist), or completed hands.
 */
export function isScorecardPendingVerification(sc: {
  isMatchConfirmed?: boolean;
  matchStage?: string | null;
  team1Score?: number;
  team2Score?: number;
  hands?: any[];
}): boolean {
  if (sc.isMatchConfirmed) return false;
  if (sc.matchStage === 'summary') return true;
  const t1 = sc.team1Score || 0;
  const t2 = sc.team2Score || 0;
  if (t1 >= 7 || t2 >= 7) return true;
  const rawHands = sc.hands ? (Array.isArray(sc.hands) ? sc.hands : Object.values(sc.hands)) : [];
  if (rawHands.length >= 3) return true;
  if (rawHands.length >= 2) {
    const h1W = (rawHands[0]?.pointsTeam1 || 0) > (rawHands[0]?.pointsTeam2 || 0) ? 1 : (rawHands[0]?.pointsTeam2 || 0) > (rawHands[0]?.pointsTeam1 || 0) ? 2 : 0;
    const h2W = (rawHands[1]?.pointsTeam1 || 0) > (rawHands[1]?.pointsTeam2 || 0) ? 1 : (rawHands[1]?.pointsTeam2 || 0) > (rawHands[1]?.pointsTeam1 || 0) ? 2 : 0;
    if (h1W !== 0 && h1W === h2W) return true;
  }
  return false;
}

/**
 * Subscribes to pending scorecards where the logged-in user is a participant.
 * Powers the global in-app notification banner for regular members.
 */
export function subscribeToUserPendingScorecards(
  userIdOrIds: string | string[],
  userNames: string[] = [],
  callback: (scorecards: PendingScorecardSummary[]) => void
): () => void {
  const candidateIds = (Array.isArray(userIdOrIds) ? userIdOrIds : [userIdOrIds]).filter(Boolean);
  if (candidateIds.length === 0 && userNames.length === 0) return () => {};

  try {
    rtdbGoOnline(rtdb);
  } catch {}

  const allScorecardsRef = rtdbRef(rtdb, 'live_scorecards');
  let lastSig = '__INIT__';

  const processRawMap = (val: Record<string, LiveScorecardData> | null | undefined) => {
    const list: PendingScorecardSummary[] = [];
    if (val && typeof val === 'object') {
      for (const raw of Object.values(val)) {
        const sc = raw as LiveScorecardData;
        if (!sc || !sc.scorecardId) continue;

        const isUserCreator = isScorecardPlayerMatch(
          { id: sc.creatorId, name: sc.creatorName },
          candidateIds,
          userNames
        );

        const isUserPartner = isScorecardPlayerMatch(
          { id: sc.partnerId, name: sc.partnerName },
          candidateIds,
          userNames
        );

        const isUserOpponent =
          isScorecardPlayerMatch({ id: sc.opponent1Id, name: sc.opponent1Name }, candidateIds, userNames) ||
          isScorecardPlayerMatch({ id: sc.opponent2Id, name: sc.opponent2Name }, candidateIds, userNames);

        // Include user if:
        // 1. Partner or Opponent
        // 2. OR Creator when match is confirmed (waiting on creator submission)
        if (!isUserPartner && !isUserOpponent && !(isUserCreator && sc.isMatchConfirmed)) {
          continue;
        }

        let unconfirmedHandIndex: number | undefined = undefined;
        let unconfirmedHandNumber: number | undefined = undefined;
        let latestHandSummary = 'Match in progress';

        const rawHands: GameHand[] = sc.hands ? (Array.isArray(sc.hands) ? sc.hands : (Object.values(sc.hands) as GameHand[])) : [];

        if (rawHands.length > 0) {
          const lastHand = rawHands[rawHands.length - 1];
          const bidSuit = lastHand.trumpType === 'no_trump' ? 'NT' : (lastHand.trumpSuit?.toUpperCase() || 'TRUMP');
          latestHandSummary = `Hand ${lastHand.handNumber}: ${lastHand.bid} ${bidSuit} ${lastHand.outcome === 'made' ? 'Made' : 'Set'}`;

          const unconfirmed = rawHands.find((h: any) => !h.isConfirmed);
          if (unconfirmed) {
            unconfirmedHandIndex = unconfirmed.handNumber - 1;
            unconfirmedHandNumber = unconfirmed.handNumber;
          }
        }

        list.push({
          scorecardId: sc.scorecardId,
          creatorId: sc.creatorId,
          creatorName: sc.creatorName,
          creatorPhotoUrl: sc.creatorPhotoUrl,
          partnerId: sc.partnerId,
          partnerName: sc.partnerName,
          partnerPhotoUrl: sc.partnerPhotoUrl,
          opponent1Id: sc.opponent1Id,
          opponent1Name: sc.opponent1Name,
          opponent1PhotoUrl: sc.opponent1PhotoUrl,
          opponent2Id: sc.opponent2Id,
          opponent2Name: sc.opponent2Name,
          opponent2PhotoUrl: sc.opponent2PhotoUrl,
          team1Score: sc.team1Score || 0,
          team2Score: sc.team2Score || 0,
          handsCount: rawHands.length,
          latestHandSummary,
          unconfirmedHandIndex,
          unconfirmedHandNumber,
          isUserCreator: Boolean(isUserCreator),
          isUserPartner,
          isUserOpponent,
          isSuperUser: false,
          isMatchConfirmed: Boolean(sc.isMatchConfirmed),
          confirmedByMemberId: sc.confirmedByMemberId,
          confirmedByMemberName: sc.confirmedByMemberName,
          confirmedAt: sc.confirmedAt,
          hands: rawHands,
          matchStage: sc.matchStage,
          isPendingVerification: isScorecardPendingVerification({
            isMatchConfirmed: sc.isMatchConfirmed,
            matchStage: sc.matchStage,
            team1Score: sc.team1Score,
            team2Score: sc.team2Score,
            hands: rawHands
          }),
          updatedAt: sc.updatedAt || 0
        });
      }
    }

    list.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const nextSig = JSON.stringify(list);
    if (nextSig === lastSig) return;
    lastSig = nextSig;
    callback(list);
  };

  const handleSnap = (snap: any) => {
    processRawMap(snap.exists() ? snap.val() : null);
  };

  const pullRest = () => {
    getRtdbRestWithAuth<Record<string, LiveScorecardData>>('live_scorecards')
      .then((res) => {
        if (res.ok) {
          processRawMap(res.data);
        }
      })
      .catch(() => {});
  };

  // Instant HTTP REST pull (~50ms) + SDK pull so notification renders without waiting for WebSocket handshake
  pullRest();
  rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});

  const unsubscribe = rtdbOnValue(allScorecardsRef, handleSnap);

  const handleWake = () => {
    try {
      rtdbGoOnline(rtdb);
    } catch {}
    pullRest();
    rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      handleWake();
    }
  };

  const intervalId = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      pullRest();
    }
  }, FAST_POLL_INTERVAL_MS);

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', handleWake);
    window.addEventListener('pageshow', handleWake);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  return () => {
    clearInterval(intervalId);
    unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', handleWake);
      window.removeEventListener('pageshow', handleWake);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };
}

/**
 * Subscribes to all active live scorecards across the club.
 * Used by Super Users / Admins to monitor and audit all in-progress matches in real time.
 */
export function subscribeToAllLiveScorecards(
  userIdOrIds: string | string[],
  callback: (scorecards: PendingScorecardSummary[]) => void,
  userNames?: string[]
): () => void {
  const candidateIds = (Array.isArray(userIdOrIds) ? userIdOrIds : [userIdOrIds]).filter(Boolean);
  const candidateNames = (userNames || []).filter(Boolean);
  try {
    rtdbGoOnline(rtdb);
  } catch {}

  const allScorecardsRef = rtdbRef(rtdb, 'live_scorecards');
  let lastSig = '__INIT__';

  const processRawMap = (val: Record<string, LiveScorecardData> | null | undefined) => {
    const list: PendingScorecardSummary[] = [];
    if (val && typeof val === 'object') {
      for (const raw of Object.values(val)) {
        const sc = raw as LiveScorecardData;
        if (!sc || !sc.scorecardId) continue;

        let unconfirmedHandIndex: number | undefined = undefined;
        let unconfirmedHandNumber: number | undefined = undefined;
        let latestHandSummary = 'Match in progress';

        const rawHands: GameHand[] = sc.hands ? (Array.isArray(sc.hands) ? sc.hands : (Object.values(sc.hands) as GameHand[])) : [];

        if (rawHands.length > 0) {
          const lastHand = rawHands[rawHands.length - 1];
          const bidSuit = lastHand.trumpType === 'no_trump' ? 'NT' : (lastHand.trumpSuit?.toUpperCase() || 'TRUMP');
          latestHandSummary = `Hand ${lastHand.handNumber}: ${lastHand.bid} ${bidSuit} ${lastHand.outcome === 'made' ? 'Made' : 'Set'}`;

          const unconfirmed = rawHands.find((h) => !h.isConfirmed);
          if (unconfirmed) {
            unconfirmedHandIndex = unconfirmed.handNumber - 1;
            unconfirmedHandNumber = unconfirmed.handNumber;
          }
        }

        const isUserCreator = isScorecardPlayerMatch(
          { id: sc.creatorId, name: sc.creatorName },
          candidateIds,
          candidateNames
        );
        const isUserPartner = isScorecardPlayerMatch(
          { id: sc.partnerId, name: sc.partnerName },
          candidateIds,
          candidateNames
        );
        const isUserOpponent =
          isScorecardPlayerMatch({ id: sc.opponent1Id, name: sc.opponent1Name }, candidateIds, candidateNames) ||
          isScorecardPlayerMatch({ id: sc.opponent2Id, name: sc.opponent2Name }, candidateIds, candidateNames);

        list.push({
          scorecardId: sc.scorecardId,
          creatorId: sc.creatorId,
          creatorName: sc.creatorName,
          creatorPhotoUrl: sc.creatorPhotoUrl,
          partnerId: sc.partnerId,
          partnerName: sc.partnerName,
          partnerPhotoUrl: sc.partnerPhotoUrl,
          opponent1Id: sc.opponent1Id,
          opponent1Name: sc.opponent1Name,
          opponent1PhotoUrl: sc.opponent1PhotoUrl,
          opponent2Id: sc.opponent2Id,
          opponent2Name: sc.opponent2Name,
          opponent2PhotoUrl: sc.opponent2PhotoUrl,
          team1Score: sc.team1Score || 0,
          team2Score: sc.team2Score || 0,
          handsCount: rawHands.length,
          latestHandSummary,
          unconfirmedHandIndex,
          unconfirmedHandNumber,
          isUserCreator: Boolean(isUserCreator),
          isUserPartner,
          isUserOpponent,
          isSuperUser: true,
          isMatchConfirmed: Boolean(sc.isMatchConfirmed),
          confirmedByMemberId: sc.confirmedByMemberId,
          confirmedByMemberName: sc.confirmedByMemberName,
          confirmedAt: sc.confirmedAt,
          hands: rawHands,
          matchStage: sc.matchStage,
          isPendingVerification: isScorecardPendingVerification({
            isMatchConfirmed: sc.isMatchConfirmed,
            matchStage: sc.matchStage,
            team1Score: sc.team1Score,
            team2Score: sc.team2Score,
            hands: rawHands
          }),
          updatedAt: sc.updatedAt || 0
        });
      }
    }

    list.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const nextSig = JSON.stringify(list);
    if (nextSig === lastSig) return;
    lastSig = nextSig;
    callback(list);
  };

  const handleSnap = (snap: any) => {
    processRawMap(snap.exists() ? snap.val() : null);
  };

  const pullRest = () => {
    getRtdbRestWithAuth<Record<string, LiveScorecardData>>('live_scorecards')
      .then((res) => {
        if (res.ok) {
          processRawMap(res.data);
        }
      })
      .catch(() => {});
  };

  pullRest();
  rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});

  const unsubscribe = rtdbOnValue(allScorecardsRef, handleSnap);

  const handleWake = () => {
    try {
      rtdbGoOnline(rtdb);
    } catch {}
    pullRest();
    rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      handleWake();
    }
  };

  const intervalId = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      pullRest();
    }
  }, FAST_POLL_INTERVAL_MS);

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', handleWake);
    window.addEventListener('pageshow', handleWake);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  return () => {
    clearInterval(intervalId);
    unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', handleWake);
      window.removeEventListener('pageshow', handleWake);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };
}

/**
 * Subscribes to the raw live scorecards map in RTDB.
 * Used for detecting active match participation and enforcing concurrency limits.
 */
export function subscribeToLiveScorecardsMap(
  callback: (scorecardsMap: Record<string, LiveScorecardData>) => void
): () => void {
  try {
    rtdbGoOnline(rtdb);
  } catch {}

  const allScorecardsRef = rtdbRef(rtdb, 'live_scorecards');
  let lastSig = '__INIT__';

  const emitMap = (map: Record<string, LiveScorecardData>) => {
    const nextSig = JSON.stringify(map);
    if (nextSig === lastSig) return;
    lastSig = nextSig;
    callback(map);
  };

  const handleSnap = (snap: any) => {
    if (snap.exists()) {
      emitMap((snap.val() || {}) as Record<string, LiveScorecardData>);
    } else {
      emitMap({});
    }
  };

  const pullRest = () => {
    getRtdbRestWithAuth<Record<string, LiveScorecardData>>('live_scorecards')
      .then((res) => {
        if (res.ok) {
          emitMap(res.data || {});
        }
      })
      .catch(() => {});
  };

  pullRest();
  rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});

  const unsubscribe = rtdbOnValue(allScorecardsRef, handleSnap);

  const handleWake = () => {
    try {
      rtdbGoOnline(rtdb);
    } catch {}
    pullRest();
    rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      handleWake();
    }
  };

  const intervalId = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      pullRest();
    }
  }, FAST_POLL_INTERVAL_MS);

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', handleWake);
    window.addEventListener('pageshow', handleWake);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  return () => {
    clearInterval(intervalId);
    unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', handleWake);
      window.removeEventListener('pageshow', handleWake);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };
}

/**
 * Checks if a user is currently participating in an active live scorecard.
 * Returns the scorecard data and user's role (creator, partner, or opponent) if found.
 */
export function findUserActiveScorecard(
  userIdOrIds: string | string[],
  scorecardsMap: Record<string, LiveScorecardData>,
  excludeScorecardId?: string,
  userNames?: string[]
): { scorecard: LiveScorecardData; role: 'creator' | 'partner' | 'opponent' } | null {
  const candidateIds = (Array.isArray(userIdOrIds) ? userIdOrIds : [userIdOrIds]).filter(Boolean);
  const candidateNames = (userNames || []).filter(Boolean);
  if (candidateIds.length === 0 && candidateNames.length === 0) return null;
  if (!scorecardsMap) return null;

  for (const sc of Object.values(scorecardsMap)) {
    if (!sc || !sc.scorecardId || sc.scorecardId === excludeScorecardId) continue;

    if (isScorecardPlayerMatch({ id: sc.creatorId, name: sc.creatorName }, candidateIds, candidateNames)) {
      return { scorecard: sc, role: 'creator' };
    }
    if (isScorecardPlayerMatch({ id: sc.partnerId, name: sc.partnerName }, candidateIds, candidateNames)) {
      return { scorecard: sc, role: 'partner' };
    }
    if (
      isScorecardPlayerMatch({ id: sc.opponent1Id, name: sc.opponent1Name }, candidateIds, candidateNames) ||
      isScorecardPlayerMatch({ id: sc.opponent2Id, name: sc.opponent2Name }, candidateIds, candidateNames)
    ) {
      return { scorecard: sc, role: 'opponent' };
    }
  }

  return null;
}

/**
 * Generates a map of all member IDs and name tokens who are actively participating in other live scorecards.
 * Used by player pickers and QR scanners to prevent adding busy players to conflicting matches.
 */
export function getBusyMembersMap(
  scorecardsMap: Record<string, LiveScorecardData>,
  excludeScorecardId?: string
): Record<string, { scorecardId: string; creatorName: string }> {
  const busy: Record<string, { scorecardId: string; creatorName: string }> = {};
  if (!scorecardsMap) return busy;

  for (const sc of Object.values(scorecardsMap)) {
    if (!sc || !sc.scorecardId || sc.scorecardId === excludeScorecardId) continue;

    const creatorName = sc.creatorName || 'Member';
    const entry = { scorecardId: sc.scorecardId, creatorName };

    const registerPlayer = (id?: string | null, name?: string | null) => {
      if (id) {
        const trimmed = id.trim();
        busy[trimmed] = entry;
        busy[trimmed.toLowerCase()] = entry;
      }
      if (name) {
        const clean = name.trim().toLowerCase();
        if (clean) {
          busy[clean] = entry;
          const tokens = clean.split(/\s+/).filter(Boolean);
          for (const tok of tokens) {
            if (tok.length >= 2) {
              busy[tok] = entry;
            }
          }
        }
      }
    };

    registerPlayer(sc.creatorId, sc.creatorName);
    registerPlayer(sc.partnerId, sc.partnerName);
    registerPlayer(sc.opponent1Id, sc.opponent1Name);
    registerPlayer(sc.opponent2Id, sc.opponent2Name);
  }

  return busy;
}

/**
 * Confirms a hand from an opponent or Super User device in real time.
 */
export async function confirmLiveScorecardHand(
  scorecardId: string,
  handIndex: number,
  confirmedByMemberId: string,
  confirmedByName: string
): Promise<void> {
  if (!scorecardId) return;

  try {
    let data: LiveScorecardData | null = null;
    const restRes = await getRtdbRestWithAuth<LiveScorecardData>(`live_scorecards/${scorecardId}`);
    if (restRes.ok && restRes.data) {
      data = restRes.data;
    } else {
      const liveRef = rtdbRef(rtdb, `live_scorecards/${scorecardId}`);
      const snap = await rtdbGet(liveRef);
      if (snap.exists()) {
        data = snap.val() as LiveScorecardData;
      }
    }

    if (!data || !data.hands || !data.hands[handIndex]) return;

    const updatedHands = [...data.hands];
    updatedHands[handIndex] = {
      ...updatedHands[handIndex],
      isConfirmed: true,
      confirmedByMemberId,
      confirmedAt: new Date().toISOString()
    };

    const updatedData: LiveScorecardData = {
      ...data,
      hands: updatedHands,
      updatedAt: Date.now()
    };

    await publishLiveScorecard(updatedData);
  } catch (err) {
    console.error('[liveScorecardSyncService] Error confirming hand:', err);
    throw err;
  }
}

/**
 * Confirms all hands in the match in real time (one-tap full match approval).
 */
export async function confirmLiveScorecardMatch(
  scorecardId: string,
  confirmedByMemberId: string,
  confirmedByName: string
): Promise<void> {
  if (!scorecardId) return;

  try {
    let data: LiveScorecardData | null = null;
    const restRes = await getRtdbRestWithAuth<LiveScorecardData>(`live_scorecards/${scorecardId}`);
    if (restRes.ok && restRes.data) {
      data = restRes.data;
    } else {
      const liveRef = rtdbRef(rtdb, `live_scorecards/${scorecardId}`);
      const snap = await rtdbGet(liveRef);
      if (snap.exists()) {
        data = snap.val() as LiveScorecardData;
      }
    }

    if (!data) return;

    const nowIso = new Date().toISOString();
    const rawHands = data.hands ? (Array.isArray(data.hands) ? data.hands : Object.values(data.hands)) : [];
    const updatedHands = rawHands.map((h: any) => ({
      ...h,
      isConfirmed: true,
      confirmedByMemberId,
      confirmedAt: h.confirmedAt || nowIso
    }));

    const updatedData: LiveScorecardData = {
      ...data,
      isMatchConfirmed: true,
      confirmedByMemberId,
      confirmedByMemberName: confirmedByName,
      confirmedAt: nowIso,
      hands: updatedHands,
      updatedAt: Date.now()
    };

    await publishLiveScorecard(updatedData);
  } catch (err) {
    console.error('[liveScorecardSyncService] Error confirming match:', err);
    throw err;
  }
}

/**
 * Clears the live scorecard and user queues when a match is finalized or cancelled.
 */
export async function removeLiveScorecard(
  scorecardId: string,
  involvedUserIds: string[]
): Promise<void> {
  if (!scorecardId) return;

  try {
    const sdkRemovalPromises = [
      rtdbRemove(rtdbRef(rtdb, `live_scorecards/${scorecardId}`))
    ];

    for (const uid of involvedUserIds) {
      if (uid) {
        sdkRemovalPromises.push(
          rtdbRemove(rtdbRef(rtdb, `user_live_scorecards/${uid}/${scorecardId}`))
        );
      }
    }

    const restDeleted = await deleteRtdbRestWithAuth(`live_scorecards/${scorecardId}`);
    if (!restDeleted) {
      await Promise.all(sdkRemovalPromises);
    } else {
      Promise.all(sdkRemovalPromises).catch(() => {});
    }

    console.log(`[liveScorecardSyncService] Removed live scorecard ${scorecardId}`);
  } catch (err: any) {
    console.error('[liveScorecardSyncService] Error removing live scorecard:', err);
    try {
      await writeAuditLog(
        'Scorecard RTDB Remove Error',
        scorecardId,
        `Failed to remove live scorecard ${scorecardId}: ${err?.message || String(err)}`
      );
    } catch {}
  }
}


/**
 * Updates the scores and optionally the hand breakdown of a pending live scorecard.
 * Persists changes immediately to RTDB so participants see the adjustments in real time.
 */
export interface UpdateLiveScorecardOptions {
  team1Score: number;
  team2Score: number;
  hands?: GameHand[];
  creatorId?: string;
  creatorName?: string;
  creatorPhotoUrl?: string | null;
  partnerId?: string | null;
  partnerName?: string | null;
  partnerPhotoUrl?: string | null;
  opponent1Id?: string | null;
  opponent1Name?: string | null;
  opponent1PhotoUrl?: string | null;
  opponent2Id?: string | null;
  opponent2Name?: string | null;
  opponent2PhotoUrl?: string | null;
}

export async function updateLiveScorecardScores(
  scorecardId: string,
  scores: UpdateLiveScorecardOptions
): Promise<void> {
  if (!scorecardId) return;

  let current: LiveScorecardData | null = null;
  const restRes = await getRtdbRestWithAuth<LiveScorecardData>(`live_scorecards/${scorecardId}`);
  if (restRes.ok && restRes.data) {
    current = restRes.data;
  } else {
    const liveRef = rtdbRef(rtdb, `live_scorecards/${scorecardId}`);
    const snap = await rtdbGet(liveRef);
    if (snap.exists()) {
      current = snap.val() as LiveScorecardData;
    }
  }

  if (!current) {
    throw new Error(`Scorecard ${scorecardId} not found.`);
  }

  const oldPlayerIds = [
    current.creatorId,
    current.partnerId,
    current.opponent1Id,
    current.opponent2Id
  ].filter((id): id is string => Boolean(id));

  const updatedHands = scores.hands !== undefined ? scores.hands : current.hands;

  const nextCreatorId = scores.creatorId !== undefined ? scores.creatorId : current.creatorId;
  const nextCreatorName = scores.creatorName !== undefined ? scores.creatorName : current.creatorName;
  const nextCreatorPhotoUrl = scores.creatorPhotoUrl !== undefined ? scores.creatorPhotoUrl : current.creatorPhotoUrl;

  const nextPartnerId = scores.partnerId !== undefined ? scores.partnerId : current.partnerId;
  const nextPartnerName = scores.partnerName !== undefined ? scores.partnerName : current.partnerName;
  const nextPartnerPhotoUrl = scores.partnerPhotoUrl !== undefined ? scores.partnerPhotoUrl : current.partnerPhotoUrl;

  const nextOpponent1Id = scores.opponent1Id !== undefined ? scores.opponent1Id : current.opponent1Id;
  const nextOpponent1Name = scores.opponent1Name !== undefined ? scores.opponent1Name : current.opponent1Name;
  const nextOpponent1PhotoUrl = scores.opponent1PhotoUrl !== undefined ? scores.opponent1PhotoUrl : current.opponent1PhotoUrl;

  const nextOpponent2Id = scores.opponent2Id !== undefined ? scores.opponent2Id : current.opponent2Id;
  const nextOpponent2Name = scores.opponent2Name !== undefined ? scores.opponent2Name : current.opponent2Name;
  const nextOpponent2PhotoUrl = scores.opponent2PhotoUrl !== undefined ? scores.opponent2PhotoUrl : current.opponent2PhotoUrl;

  const newPlayerIds = [
    nextCreatorId,
    nextPartnerId,
    nextOpponent1Id,
    nextOpponent2Id
  ].filter((id): id is string => Boolean(id));

  // Clean up any removed players from their personal live queues in RTDB
  const removedPlayerIds = oldPlayerIds.filter((oldId) => !newPlayerIds.includes(oldId));
  for (const remUid of removedPlayerIds) {
    try {
      rtdbRemove(rtdbRef(rtdb, `user_live_scorecards/${remUid}/${scorecardId}`)).catch(() => {});
      deleteRtdbRestWithAuth(`user_live_scorecards/${remUid}/${scorecardId}`).catch(() => {});
    } catch {}
  }

  const updatedData: LiveScorecardData = {
    ...current,
    team1Score: scores.team1Score,
    team2Score: scores.team2Score,
    hands: updatedHands,
    creatorId: nextCreatorId,
    creatorName: nextCreatorName,
    creatorPhotoUrl: nextCreatorPhotoUrl,
    partnerId: nextPartnerId,
    partnerName: nextPartnerName,
    partnerPhotoUrl: nextPartnerPhotoUrl,
    opponent1Id: nextOpponent1Id,
    opponent1Name: nextOpponent1Name,
    opponent1PhotoUrl: nextOpponent1PhotoUrl,
    opponent2Id: nextOpponent2Id,
    opponent2Name: nextOpponent2Name,
    opponent2PhotoUrl: nextOpponent2PhotoUrl,
    updatedAt: Date.now()
  };

  await publishLiveScorecard(updatedData);
}

/**
 * Commits a verified live scorecard to official Firestore match records
 * and cleans up the active live queues in RTDB.
 */
export async function commitLiveScorecardAsGameRecord(
  scorecard: LiveScorecardData,
  adminUser?: { uid: string; displayName?: string | null }
): Promise<{ success: boolean; message: string; gameId?: string }> {
  if (!scorecard || !scorecard.scorecardId) {
    return { success: false, message: 'Invalid scorecard data.' };
  }

  try {
    const team1Final = scorecard.team1Score || 0;
    const team2Final = scorecard.team2Score || 0;
    const nowIso = new Date().toISOString();
    const adminUid = adminUser?.uid || 'admin';
    const adminName = adminUser?.displayName || 'Club Admin';

    const rawHands = scorecard.hands
      ? Array.isArray(scorecard.hands)
        ? scorecard.hands
        : Object.values(scorecard.hands)
      : [];

    const matchDoc: Partial<GameRecord> = {
      playedDate: scorecard.playedDate || nowIso.slice(0, 10),
      locationName: scorecard.locationName || 'Clubhouse',
      scorecardId: scorecard.scorecardId,
      scorecardOrigin: 'digital_app',
      confirmationMode: 'match_end',
      team1Name: `${scorecard.creatorName || 'Team 1'} & ${scorecard.partnerName || 'Partner'}`,
      team1Player1Id: scorecard.creatorId,
      team1Player1Name: scorecard.creatorName || 'Member',
      team1Player2Id: scorecard.partnerId || undefined,
      team1Player2Name: scorecard.partnerName || undefined,
      team2Name: `${scorecard.opponent1Name || 'Opponent 1'} & ${scorecard.opponent2Name || 'Opponent 2'}`,
      team2Player1Id: scorecard.opponent1Id || undefined,
      team2Player1Name: scorecard.opponent1Name || 'Opponent 1',
      team2Player2Id: scorecard.opponent2Id || undefined,
      team2Player2Name: scorecard.opponent2Name || undefined,
      team1FinalScore: team1Final,
      team2FinalScore: team2Final,
      winningTeam: team1Final > team2Final ? 1 : team2Final > team1Final ? 2 : 1,
      team1Initials: (scorecard.creatorName || 'T1').slice(0, 3).toUpperCase(),
      team2Initials: (scorecard.confirmedByMemberName || scorecard.opponent1Name || 'T2').slice(0, 3).toUpperCase(),
      isConfirmed: true,
      confirmedByMemberId: scorecard.confirmedByMemberId || adminUid,
      confirmedByMemberName: scorecard.confirmedByMemberName || adminName,
      confirmedAt: scorecard.confirmedAt || nowIso,
      hands: (rawHands as any[]).map((h: any, i: number) => ({
        ...h,
        handNumber: h.handNumber || i + 1,
        bidderPlayerId: h.bidderPlayerId || undefined,
        bidderPlayerName: h.bidderPlayerName || undefined,
        isConfirmed: true,
        confirmedByMemberId: scorecard.confirmedByMemberId || adminUid,
        confirmedAt: scorecard.confirmedAt || nowIso
      })),
      auditLog: [
        {
          action: 'scorecard_submitted',
          performedByUid: adminUid,
          performedByName: adminName,
          timestamp: nowIso,
          details: `Scorecard ${scorecard.scorecardId} committed by ${adminName}. Scored by ${scorecard.creatorName}. Final: ${team1Final}-${team2Final}.`
        },
        {
          action: 'scorecard_verified',
          performedByUid: scorecard.confirmedByMemberId || adminUid,
          performedByName: scorecard.confirmedByMemberName || adminName,
          timestamp: scorecard.confirmedAt || nowIso,
          details: `Match officially verified and approved by ${scorecard.confirmedByMemberName || adminName}.`
        }
      ]
    };

    const res = await saveGameRecord(matchDoc);
    if (res.success) {
      await removeLiveScorecard(scorecard.scorecardId, [
        scorecard.creatorId,
        scorecard.partnerId,
        scorecard.opponent1Id,
        scorecard.opponent2Id
      ].filter((id): id is string => Boolean(id)));
      return { success: true, message: 'Scorecard committed to official match records.', gameId: res.id };
    }
    return { success: false, message: res.message || 'Failed to save game record.' };
  } catch (err: any) {
    console.error('[liveScorecardSyncService] Error committing scorecard:', err);
    return { success: false, message: err?.message || 'Failed to commit scorecard.' };
  }
}
